import rollbar from 'shared-assets/src/errors/errors';
import featureToggles from 'shared-assets/lib/sdk/featureToggles';
import {
  hideLegacyNavigation,
  mountNavigationUiWithFallback,
  remountNavigationUi,
  showLegacyNavigation,
} from './navigation-v2-loader';

const NAV_SELECTOR = '#nav-v2';
const RECOVERY_DELAY_MS = 300;

// Consolidated state
const state = {
  handle: null,
  initPromise: null,
  recoveryPromise: null,
  observer: null,
  options: null,
  uiName: null,
  recoveryTimer: null,
  cleanupRegistered: false,
};

/**
 * Initialize navigation-v2 for a UI package.
 * @param {string} uiName - Name of the UI package (e.g., 'admin-ui')
 * @param {Object} [options] - Optional overrides for mountNavigationUiWithFallback
 */
export function initNavigationV2(uiName, options = {}) {
  if (state.handle?.mounted) return Promise.resolve(state.handle);
  if (state.initPromise) return state.initPromise;

  state.options = options;
  state.uiName = uiName;

  state.initPromise = mountNavigationUiWithFallback({
    systemJsAmdUrl: null,
    logger: rollbar,
    shouldMount: () => featureToggles.isEnabled('dark-feature.navigation-v2'),
    onMountSuccess: () => console.info(`${uiName}: navigation-v2 mounted`), // eslint-disable-line no-console
    onMountSkipped: () => console.info(`${uiName}: navigation-v2 skipped`), // eslint-disable-line no-console
    onMountFailure: err => rollbar.error(`${uiName}: navigation-v2 failed`, err),
    ...options,
  })
    .then(handle => {
      state.handle = handle;
      if (handle?.mounted) {
        startObserver();
      }
      return handle;
    })
    .finally(() => {
      state.initPromise = null;
    });

  registerCleanup();
  return state.initPromise;
}

export { mountNavigationUiWithFallback };

function registerCleanup() {
  if (state.cleanupRegistered) return;
  state.cleanupRegistered = true;

  // Keep listeners active for the life of the document.
  // Unload-time teardown can fire during history transitions and leave nav unmounted on back.
  window.addEventListener('hashchange', onNavigationEvent);
  window.addEventListener('popstate', onNavigationEvent);
  window.addEventListener('pageshow', onNavigationEvent);
}

function needsRecovery(targetElement) {
  const currentElement = state.handle?.navProps?.domElement;

  // Element changed or removed from DOM
  if (!targetElement) return false;
  if (!currentElement) return true;
  if (targetElement !== currentElement) return true;
  if (!document.documentElement.contains(currentElement)) return true;

  // Nav content missing
  if (targetElement.childElementCount === 0) return true;
  if (!targetElement.querySelector('.MuiToolbar-root')) return true;

  const rect = targetElement.getBoundingClientRect();
  if (!rect || rect.height <= 0) return true;

  return false;
}

function attemptRecovery() {
  if (!state.handle?.mounted || state.recoveryPromise || state.initPromise) return;

  const targetElement = document.querySelector(NAV_SELECTOR);
  if (!needsRecovery(targetElement)) return;

  showLegacyNavigation();
  state.recoveryPromise = remountNavigationUi(state.handle, targetElement)
    .then(nextHandle => {
      state.handle = nextHandle;
      hideLegacyNavigation();
      console.info(`${state.uiName}: navigation-v2 recovered`); // eslint-disable-line no-console
    })
    .catch(error => {
      rollbar.error(`${state.uiName}: navigation-v2 recovery failed`, error);
    })
    .finally(() => {
      state.recoveryPromise = null;
    });
}

function scheduleRecoveryCheck() {
  clearTimeout(state.recoveryTimer);
  state.recoveryTimer = setTimeout(attemptRecovery, RECOVERY_DELAY_MS);
}

function onNavigationEvent() {
  if (state.initPromise || state.recoveryPromise) return;

  const targetElement = document.querySelector(NAV_SELECTOR);

  // If nav is mounted, check if recovery needed
  if (state.handle?.mounted) {
    if (!state.observer) startObserver();
    if (!targetElement) return;
    scheduleRecoveryCheck();
    return;
  }

  if (!targetElement) return;

  // If mount was intentionally skipped (e.g., feature off), avoid retrying
  // on every hash/popstate/pageshow event. Only retry when there was an error.
  const wasSkippedWithoutError = state.handle?.mounted === false && !state.handle?.error;
  if (wasSkippedWithoutError) return;

  // If nav never mounted successfully, try again
  showLegacyNavigation();
  initNavigationV2(state.uiName, state.options || {});
}

function startObserver() {
  if (state.observer) return;

  const targetElement = document.querySelector(NAV_SELECTOR);
  const observeRoot = targetElement?.parentNode || document.body;
  if (!observeRoot) return;

  state.observer = new MutationObserver(() => {
    const targetElement = document.querySelector(NAV_SELECTOR);
    if (needsRecovery(targetElement) && !state.recoveryPromise && !state.initPromise) {
      attemptRecovery();
    }
  });

  state.observer.observe(observeRoot, { childList: true, subtree: true });

  // Initial check after mount
  scheduleRecoveryCheck();
}
